// @flow

import React, { PureComponent, Fragment } from 'react';
import type { ElementRef, Node } from 'react';
import { change, initialize } from 'redux-form';
import { Grid, Row, Col } from 'Common/Grid';
import LeasingCard from 'Common/LeasingCard';
import { connect } from 'react-redux';
import throttle from 'lodash.throttle';
import { Bling as GPT } from 'react-gpt';
import classNames from 'classnames';
import styles from 'rootContainer/Results.scss';
import SortBy from 'Common/SortBy';

import BreadCrumb from 'Common/BreadCrumb';

import FilterForm from 'Common/FilterForm';
import Icon from 'Common/Icon';
import BrowseAll from 'Common/BrowseAll';
import SEO from 'Common/Seo';
import ErrorBox from 'Common/ErrorBox';
import Pagination from 'Common/Pagination';
import getFilterBreakdownProp from 'Common/getFilterBreakdownProp';
import buildUrlFromForm from 'Common/buildUrlFromForm';
import buildQuery from 'Common/buildQuery';
import { getKey } from 'Common/formValues';
import {
  slots,
  createAdUnitPath,
  targetingResults,
  adUnitPageNames
} from 'Common/ads';
import metadata from 'App/variables/seo';
import scrollToTopOfPage from 'Common/scrollToTopOfPage';
import {
  gtmResults,
  gtmDisplayResults,
  gtmResultClick,
  gtmPageview
} from 'Common/gtmPayloads';
import { gtmEventPush } from 'utils/gtm/events';
import isWindow from 'Common/isWindow';
import isWindowProp from 'Common/isWindowProp';
import chunk from 'Common/chunk';
import * as Actions from 'actionTypes';
import Accordion from 'Common/Accordion';
import slugify from 'slugify';
import * as R from 'ramda';
import 'url-search-params-polyfill';
import PartnershipLogo from 'Common/PartnershipLogo';
import { getDerivativeDisplayName } from 'utils/derivative';
import parsedPrice from 'Common/parsedPrice';
import { setAutocarHeaderVisibility } from 'utils/autocar';
import ActiveFilters from 'Common/ActiveFilters';
import SimpleModal from 'Common/SimpleModal';
import CarLeaseIndicator from 'Common/CarLeaseIndicator';

import { generateSeoUrl } from 'utils/generateSeoUrl';

import pageData from 'utils/pageData';

const isUrlMatch = (oldUrl, newUrl) =>
  oldUrl.pathname === newUrl.pathname && oldUrl.search === newUrl.search;

const getSelectedMake = formValues => {
  if (formValues) {
    return formValues.make ? getKey(formValues.make) : '';
  } else {
    return '';
  }
};

const createActiveFields = R.compose(
  R.keys,
  R.filter(R.identity),
  R.omit(['lat', 'long', 'nearlynew', 'used', 'sortBy'])
);

const thisPageData = pageData['car-leasing-deals'];

/**
 * <Results /> component.
 * @return {class} new results instance
 */
export class Results extends PureComponent<*, *> {
  hasInitialPushBeenDone: boolean = false;
  currentBounce: Function;
  debounce: Function;
  filterButtonRef: ?HTMLElement;
  fromUrl: boolean;
  initialValues: ?Object;
  interval: any;
  portalRef: any;

  /**
   * @param {object} props - props passed
   */
  constructor(props: Object) {
    super(props);
    this.state = {
      isResultsMode: true,
      breadCrumbUrl: props.match.url,
      stickyFilterButton: false,
      isError: false,
      delayAdMasthead: true,
      delayAdOverlay: true,
      delayAdOverlay2: true,
      initialResults: [],
      enablePriceIndicatorInfo: false,
      indicatorType: '',
      leasingPrice: 'personal'
    };
    this.portalRef = React.createRef();
  }

  /**
   * MapStateToProps implementation.
   * @return {object} new state object
   * @param {object} state object
   */
  static mapStateToProps(state: Object): Object {
    /**
     * pluckFromState
     * @param {string} prop - prop
     * @param {string} key - key
     * @return {array} - options
     */
    const pluckFromState = (prop, key) =>
      getFilterBreakdownProp(state, prop, key);

    const hasResults = !!state.results.results;
    const hasPagination =
      state.results.pagination && state.results.pagination.pageSize > 0;

    const additonalOptions = {
      fuelTypes: pluckFromState('fuelType'),
      category: pluckFromState('category'),
      colours: pluckFromState('colour'),
      doors: pluckFromState('doors'),
      seats: pluckFromState('no_of_seats'),
      transmissions: pluckFromState('transmission'),
      annualTax: pluckFromState('annualTax'),
      fuelConsumption: pluckFromState('ec_combined_mpg'),
      co2Emissions: pluckFromState('co2_gkm'),
      acceleration: pluckFromState('0_to_62_mph_secs'),
      engineSizeFrom: pluckFromState('badge_engine_ccFrom'),
      engineSizeTo: pluckFromState('badge_engine_ccTo')
    };

    return {
      cheapestResult:
        (state.results.cheapestResult &&
          state.results.cheapestResult.results) ||
        [],
      results: state.results.results,
      redirectUrl: state.results.redirectUrl,
      pagination: state.results.pagination,
      loc: state.results.location,
      isResultsLoading:
        state.results.isLoading || !hasResults || !hasPagination,
      filterForm: state.form.search,
      initialValues: state.results.initialForm,
      makes: getFilterBreakdownProp(state, 'make'),
      models: getFilterBreakdownProp(state, 'model'),
      trims: getFilterBreakdownProp(state, 'trim'),
      priceFrom: getFilterBreakdownProp(state, 'cashPriceStat', 'min'),
      priceTo: getFilterBreakdownProp(state, 'cashPriceStat', 'max'),
      ui: state.ui,
      additonalOptions,
      isMobile: state.viewport.isXs,
      isError: state.ui.error,
      viewport: state.viewport.viewport
    };
  }

  pushGtm: (item: Object) => void = item => {
    if (isWindow() && isWindowProp('dataLayer')) {
      window.dataLayer.push(gtmResultClick(item));
    }
  };

  /* eslint-disable camelcase */
  /**
   * UNSAFE_componentWillMount it fetches the content.
   * @return {void}
   */
  UNSAFE_componentWillMount() {
    const query = buildQuery(
      this.props.match.params.make,
      this.props.match.params.model,
      this.props.ui,
      this.props.location.search,
      this.props.match.params.id,
      this.props.match.url
    );

    this.fromUrl = true;
    this.initialValues = {};
    this.props.dispatch({
      type: Actions.FETCH_INITIAL_RESULTS,
      query,
      currentUrl: '/car-leasing-deals'
    });
  }

  pushPv: () => void = () => {
    if (
      isWindow() &&
      isWindowProp('dataLayer') &&
      isWindowProp('location') &&
      this.props.results &&
      !this.hasInitialPushBeenDone
    ) {
      const pageViewData = {
        content_type: 'Leasing',
        navigation_section: 'Leasing deals results'
      };
      if (this.props.filterForm.values) {
        pageViewData.make = this.props.filterForm.values.make
          ? JSON.parse(this.props.filterForm.values.make).key
          : '';
        pageViewData.model = this.props.filterForm.values.model
          ? JSON.parse(this.props.filterForm.values.model).key
          : '';
      }
      window.dataLayer.push(gtmPageview(pageViewData));
      window.dataLayer.push(gtmDisplayResults(this.props.results));

      this.hasInitialPushBeenDone = true;
      clearInterval(this.interval);
    }
  };

  componentWillUnmount() {
    clearInterval(this.interval);
    if (isWindow() && isWindowProp('dataLayer')) {
      window.dataLayer.push({ page: '' });
    }
    window.removeEventListener('scroll', this.handleStickyFilterButtonOnScroll);
    // Clear timeout to avoid memory leaks
    clearTimeout(this.delayAdMastheadTimeout);
    clearTimeout(this.delayAdOverlayTimeout);
    clearTimeout(this.delayAdOverlay2Timeout);
  }

  componentDidMount() {
    scrollToTopOfPage();
    GPT.enableSingleRequest();
    const adModel = this.props.match.params.model || '';
    targetingResults(this.props.match.params.make, adModel);
    this.interval = setInterval(this.pushPv, 300);

    window.addEventListener(
      'scroll',
      throttle(this.handleStickyFilterButtonOnScroll, 200)
    );

    // Delay loading of the top ad to prioritize side ad
    this.delayAdMastheadTimeout = setTimeout(() => {
      this.setState({ delayAdMasthead: false });
    }, 100); // Adjust delay as needed
    this.delayAdOverlayTimeout = setTimeout(() => {
      this.setState({ delayAdOverlay: false });
    }, 150); // Adjust delay as needed
    this.delayAdOverlay2Timeout = setTimeout(() => {
      this.setState({ delayAdOverlay2: false });
    }, 150); // Adjust delay as needed
  }

  /**
   * Handles the sticky Filter button on scroll on mobile
   * @param  {SyntheticInputEvent} event the onChange event for the select
   * @returns {void} void
   */
  handleStickyFilterButtonOnScroll: () => void | boolean = () => {
    if (!this.props.isMobile) {
      return false;
    }

    if (window.scrollY > 111) {
      this.setState({ stickyFilterButton: true });
    } else {
      this.setState({ stickyFilterButton: false });
    }
  };

  /**
   * setRef
   * @param el
   */
  setRef: (el: null | ElementRef<string>) => void = el => {
    this.filterButtonRef = el;
  };

  componentDidUpdate(prevProps) {
    if (prevProps.results !== this.props.results) {
      this.hasInitialPushBeenDone = false;
    }
  }

  /* eslint-disable camelcase */
  UNSAFE_componentWillReceiveProps(nextProps: Object) {
    if (
      isWindow() &&
      isWindowProp('dataLayer') &&
      isWindowProp('location') &&
      this.props.results &&
      nextProps.results &&
      nextProps.results !== this.props.results &&
      (nextProps.results !== this.props.results ||
        !isUrlMatch(nextProps.location, this.props.location)) &&
      R.difference(nextProps.results, this.props.results).length !== 0 &&
      nextProps.filterForm &&
      nextProps.filterForm.values &&
      !this.hasInitialPushBeenDone
    ) {
      const hasDealer =
        global.window.location.pathname.split('/')[2] === 'dealer';
      const dealerName = hasDealer ? nextProps.results[0].dealer.name : '';
      const pageViewData = {
        content_type: 'Leasing',
        navigation_section: 'Leasing deals results'
      };
      if (this.props.filterForm.values) {
        pageViewData.make = this.props.filterForm.values.make
          ? JSON.parse(this.props.filterForm.values.make).key
          : '';
        pageViewData.model = this.props.filterForm.values.model
          ? JSON.parse(this.props.filterForm.values.model).key
          : '';
      }
      window.dataLayer.push(gtmPageview(pageViewData));
      window.dataLayer.push(
        gtmResults(
          nextProps.filterForm.values,
          dealerName,
          nextProps.pagination.total
        )
      );
      window.dataLayer.push(gtmDisplayResults(nextProps.results));
    }

    const initialFormChanged =
      nextProps &&
      this.props &&
      nextProps.initialValues &&
      this.props.initialValues &&
      !R.equals(nextProps.initialValues, this.props.initialValues);

    if (
      (!this.props.initialValues && nextProps.initialValues) ||
      initialFormChanged
    ) {
      this.props.dispatch(initialize('search', nextProps.initialValues));
    }
    if (!this.props.results) {
      this.setState({
        ...this.state,
        initialResults: this.props.results
      });
    }

    if (!isUrlMatch(nextProps.location, this.props.location)) {
      const query = buildQuery(
        nextProps.match.params.make,
        nextProps.match.params.model,
        nextProps.ui,
        nextProps.location.search,
        this.props.match.params.id,
        this.props.match.url
      );
      this.fromUrl = true;
      this.initialValues = {};
      this.props.dispatch({
        type: Actions.FETCH_RESULTS,
        query,
        currentUrl: '/car-leasing-deals'
      });
    }
    if (isWindow() && isWindowProp('location')) {
      this.setState({
        breadCrumbUrl: window.location.pathname
      });
    }
  }

  fetchUpdatedQuery: () => void = () => {
    if (isWindow() && isWindowProp('location')) {
      const pathname = global.window.location.pathname.split('/');
      const hasDealer = pathname[2] === 'dealer';
      const isAll = pathname[2] !== 'all';
      const hasMake = !hasDealer && isAll;

      const query = buildQuery(
        hasMake ? pathname[2] : null,
        hasMake ? pathname[3] : null,
        this.props.ui,
        global.window.location.search,
        hasDealer ? pathname[3] : null,
        this.props.match.url
      );

      this.props.dispatch({
        type: Actions.FETCH_RESULTS,
        query,
        currentUrl: '/car-leasing-deals'
      });
    }
  };

  getResults: () => void = () => {
    if (!this.props.initialValues || !this.props.filterForm.values) {
      return;
    }

    const urlParams = new URLSearchParams(this.props.location.search);

    const url = buildUrlFromForm(
      this.props.filterForm,
      urlParams,
      thisPageData.name
    );

    if (isWindow() && isWindowProp('history')) {
      history.pushState({}, '', url);
      this.fetchUpdatedQuery();
    } else {
      this.props.history.push(url);
    }
  };

  getMakeAndModelText: () => string = () => {
    if (this.props.filterForm && this.props.filterForm.values) {
      const formValues = this.props.filterForm.values;
      const make = formValues.make ? getKey(formValues.make) : '';
      const model = formValues.model ? getKey(formValues.model) : '';
      return `${make} ${model}`;
    } else {
      return 'cars';
    }
  };

  toggleMode: () => void = () => {
    const isResultsMode = this.state.isResultsMode;
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: isResultsMode ? 'Go to search filters' : 'Return to results'
      }
    });

    if (
      this.props.isMobile &&
      typeof window !== 'undefined' &&
      window.document &&
      document.body
    ) {
      document.body.style.overflow = isResultsMode ? 'hidden' : 'auto';
      if (isResultsMode) {
        setAutocarHeaderVisibility(false);
      } else {
        setAutocarHeaderVisibility(true);
      }
    }

    this.setState(
      {
        isResultsMode: !isResultsMode
      },
      () => (isResultsMode ? scrollToTopOfPage() : null)
    );
  };

  renderPartnerLogo: () => Node = () => {
    return <PartnershipLogo extendStyle={styles.logo} />;
  };

  /**
   * resetField.
   * @param {string} field - field
   *
   * @return {void}
   */
  resetField: (field: string) => void = field => {
    if (field === 'nearlynew' || field === 'used') {
      this.props.dispatch(change('search', field, true));
    } else {
      this.props.dispatch(change('search', field, null));
    }

    if (field === 'advertisingLocation') {
      const url = new URL(window.location);
      url.searchParams.delete('advertisingLocation');
      this.props.dispatch(change('search', field, ''));
    }

    if (field === 'nearlyNewDeals') {
      const url = new URL(window.location);
      url.searchParams.delete('maxPlate');
      url.searchParams.delete('minPlate');
      this.props.history.push(url);
    }

    if (field === 'make') {
      this.props.dispatch(change('search', 'model', null));
      this.props.dispatch(change('search', 'trim', null));
    }

    if (field === 'model') {
      this.props.dispatch(change('search', 'trim', null));
    }
  };

  removeActiveFilter: (field: string) => void = key => {
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: 'Remove Active Filter'
      }
    });
    this.resetField(key);
  };

  resetAllFields(fields: any) {
    const urlParams = new URLSearchParams(this.props.location.search);
    gtmEventPush({
      method: 'filterForm',
      args: {
        itemLabel: 'Clear all'
      }
    });
    R.keys(fields).forEach(field => {
      this.resetField(field);
    });

    if (urlParams.get('minPlate') || urlParams.get('maxPlate')) {
      this.resetField('nearlyNewDeals');
    }
  }

  setPriceIndicatorInfoLook = e => {
    document.body.style.overflow = 'unset';
    this.setState({
      enablePriceIndicatorInfo: false
    });
  };

  toggleLeasingPrice = e => {
    this.setState({
      leasingPrice: e
    });
  };

  /**
   * Renders the about page.
   *
   * @return {JSX} about html.
   */
  render(): Node {
    const {
      results,
      isResultsLoading,
      filterForm,
      makes,
      models,
      pagination,
      priceFrom,
      priceTo,
      additonalOptions,
      initialValues,
      isMobile,
      isError
      // viewport
    } = this.props;
    const isLeasingPage = thisPageData.name === 'car-leasing-deals';

    const { classResultsFound, mtl, mtm, mts } = styles;

    const resultsLength = pagination ? pagination.total : 0;
    let formValues = initialValues || {};
    let adsSeoProps = {
      make: getKey(formValues.make),
      model: getKey(formValues.model),
      category: getKey(formValues.category),
      page: adUnitPageNames['results']
    };
    let resultsWithAds = [];
    let meta = {};
    let dealerId = getKey(formValues.dealerId);

    if (filterForm && filterForm.values) {
      formValues = filterForm.values;
      adsSeoProps = {
        make: getKey(filterForm.values.make),
        model: getKey(filterForm.values.model),
        category: getKey(filterForm.values.category),
        page: adUnitPageNames['results']
      };
      dealerId = getKey(filterForm.values.dealerId);
    }

    const selectedMake = getSelectedMake(formValues);
    const { isPostCodeValid, postCode, ...filteredFormValues } = formValues;
    delete filteredFormValues.advertisingLocation;
    const activeFilters = createActiveFields(filteredFormValues);

    const count =
      pagination && pagination.total
        ? pagination.total.toLocaleString('en')
        : 0;

    let dealerName;
    if (results && results.length > 0 && dealerId) {
      dealerName = results[0].dealer.name;
    }

    if (dealerId && results && results.length > 1) {
      adsSeoProps = { dealer: dealerName };
      meta = metadata.resultsDealer(
        adsSeoProps.dealer,
        dealerId,
        pagination,
        thisPageData
      );
    } else if (adsSeoProps.category && !adsSeoProps.make) {
      meta = metadata.resultsBodyStyle(
        adsSeoProps.category,
        count,
        pagination,
        thisPageData
      );
    } else {
      meta = metadata.resultsCar(
        adsSeoProps,
        count,
        formValues,
        pagination,
        thisPageData
      );
    }

    const urlParams = new URLSearchParams(this.props.location.search);

    const showCheapestResult = this.props.cheapestResult.length > 0;

    let nearlyNewDeals = false;
    if (urlParams.get('minPlate') || urlParams.get('maxPlate')) {
      nearlyNewDeals = true;
    }

    function transformLeasingUrl(detailsUrl) {
      return detailsUrl.replace('/car-details/', '/cars/leasing/product/');
    }

    let mappedResults = results
      ? results.map((item, index) => (
          <LeasingCard
            key={`${item.derivative.make}-${item.derivative.range}-${index}`}
            isMobile={isMobile}
            name={getDerivativeDisplayName(item.derivative)}
            make={item.derivative.make}
            range={item.derivative.range}
            engine={item.technical.badgeEngineSizeLitres}
            trim={item.derivative.trim}
            personalLeasePrice={parsedPrice(
              item &&
                item.leasingData &&
                item.leasingData.monthlyPayment &&
                item.leasingData.monthlyPayment.personal
            )}
            businessLeaselPrice={parsedPrice(
              item &&
                item.leasingData &&
                item.leasingData.monthlyPayment &&
                item.leasingData.monthlyPayment.business
            )}
            item={item}
            fuelType={item.derivative.fuelType}
            doors={item.derivative.doors}
            transmission={item.derivative.transmission}
            gcLid={urlParams.get('gclid')}
            network={urlParams.get('network')}
            gtmSectionName={'autoTraderClickThrough'}
            url={transformLeasingUrl(item.detailsUrl)}
            handleClick={this.pushGtm}
            portalRef={this.portalRef}
            dataSection={thisPageData.dataSection}
            productId={
              item && item.detailsUrl && item.detailsUrl.split('/').slice(-1)[0]
            }
            generation={item.derivative.generation}
            derivative={item.derivative.derivative}
            bodyType={item.derivative.bodyType}
            utm="CARLEASINGDEALSLINKS"
            isModelSelected={formValues.model}
            leasingPrice={this.state.leasingPrice}
          />
        ))
      : null;

    if (mappedResults) {
      let chunkedResults = chunk(mappedResults, 5);

      chunkedResults.forEach(
        (_, i) => (resultsWithAds = resultsWithAds.concat(_))
      );

      if (resultsWithAds) {
        let itemListElement = [];
        resultsWithAds.map((item, index) => {
          let make = item.props.make || '';
          let range = item.props.range || '';
          let name = item.props.name || '';
          let price =
            (item.props.item &&
              item.props.item.leasingData.monthlyPayment.business) ||
            '';
          let attentionGrabber = item.props.attentionGrabber || '';
          let fuelType = item.props.fuelType || '';
          let bodyType = item.props.bodyType || '';
          let transmission = item.props.transmission || '';
          let year = item.props.year || '';
          let description = `${year} ${name} ${fuelType} ${bodyType} with ${transmission} transmission, ${attentionGrabber}`;
          let url = generateSeoUrl(item.props.url, this.props.location) || '';
          let availability =
            (item.props.item && item.props.item.inStockLabel) || '';

          itemListElement.push({
            '@type': 'ListItem',
            position: index,
            item: {
              '@type': 'Product',
              name: `${make} ${range} ${name}`,
              // $FlowFixMe
              url: `${process.env.BASE_URL}/car-leasing-deals/${slugify(
                make
              )}/${slugify(range)}`,
              offers: {
                '@type': 'Offer',
                itemOffered: 'Car Leasing Deals',
                price: price,
                priceCurrency: 'GBP',
                url: url,
                availability: availability
              },
              description: description,
              brand: make
            }
          });
        });
        meta.schema = {
          type: 'application/ld+json',
          innerHTML: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'ItemList',
            itemListElement: itemListElement
          })
        };
      }
    }

    const stickyButtonStyle = this.state.stickyFilterButton
      ? classNames(styles.toggleModeBtnContainer, styles.stickyButton)
      : styles.toggleModeBtnContainer;
    return (
      <Fragment>
        <div
          ref={this.portalRef}
          className={styles.portal}
          data-test="portal"
        ></div>
        {!this.state.isResultsMode && (
          <div
            className={styles.filterFormBackdrop}
            onClick={this.toggleMode}
          ></div>
        )}

        <Grid className={styles.results}>
          <SEO {...meta} />
          {isError && <ErrorBox type={500} />}
          {!isError && (
            <Row>
              <Col colXs={12} colSm={12} colMd={12} colLg={12}>
                <div className={styles.header}>
                  <BreadCrumb breadCrumbUrl={this.state.breadCrumbUrl} />
                  <div className={stickyButtonStyle} ref={this.setRef}>
                    <button
                      className={styles.toggleModeBtn}
                      onClick={this.toggleMode}
                    >
                      <div className={styles.title}>
                        <Icon
                          iconName={'chevron-down-small'}
                          size={'default'}
                          className={styles.icon}
                        />
                        <span>Filters</span>
                      </div>
                      <span className={styles.numbers}>
                        (
                        {nearlyNewDeals
                          ? activeFilters.length + 1
                          : activeFilters.length}
                        ) applied
                      </span>
                    </button>
                    <SortBy
                      sortIcon={true}
                      fetchUpdatedQuery={this.fetchUpdatedQuery}
                      selectedValue={formValues.sortBy}
                      isLeasingPage={isLeasingPage}
                    />
                  </div>
                </div>
                <div className={classNames(styles.titleContainer)}>
                  <h1 className={mtl}>{meta.pageTitle}</h1>
                  {this.renderPartnerLogo()}
                </div>
                {!isError && !isMobile && !this.state.delayAdMasthead && (
                  <div className={styles.headerAd}>
                    <GPT
                      id={slots.header.id}
                      adUnitPath={createAdUnitPath(adsSeoProps)}
                      collapseEmptyDiv={true}
                      renderWhenViewable={false}
                      sizeMapping={slots.header.sizeMapping}
                      targeting={slots.header.targeting}
                    />
                  </div>
                )}
                {!isError && isMobile && (
                  <div
                    className={classNames(
                      styles.headerAd,
                      styles.headerAdSticky
                    )}
                  >
                    <GPT
                      id={slots.banner.id}
                      adUnitPath={createAdUnitPath(adsSeoProps)}
                      collapseEmptyDiv={true}
                      renderWhenViewable={false}
                      sizeMapping={slots.banner.sizeMapping}
                      targeting={slots.banner.targeting}
                    />
                  </div>
                )}
                {!showCheapestResult && (
                  <div className={styles.introContainer}>
                    {!isMobile && (
                      <p className={styles.introText}>{meta.blurb} </p>
                    )}
                    {this.renderPartnerLogo()}
                  </div>
                )}
                {showCheapestResult && (
                  <div className={styles.cheapestResultContainer}>
                    {!isMobile && (
                      <div className={styles.cheapestResultTitleTab}>
                        <h4>Our top deals</h4>
                        <div className={styles.cheapestResultTab}>
                          <span>Cheapest lease deal</span>
                        </div>
                      </div>
                    )}
                    <div className={styles.cheapestResultBox}>
                      <div className={styles.cheapestResultDetail}>
                        {isMobile && (
                          <div className={styles.cheapestResultDescription}>
                            <h4>Why lease with What Car?</h4>
                            <p className={styles.introText}>{meta.blurb}</p>
                          </div>
                        )}
                        {this.props.cheapestResult.map((item, index) => (
                          <div
                            key={item.searchId}
                            className={styles.cheapestResultItem}
                          >
                            {item.images.length > 0 && (
                              <div className={styles.cheapestResultImage}>
                                {item.inStock && (
                                  <span
                                    className={styles.cheapestResultInstock}
                                  >
                                    {item.inStockLabel}
                                  </span>
                                )}
                                <img
                                  src={item.images[0].href}
                                  alt={item.images[0].source}
                                />
                              </div>
                            )}

                            {isMobile ? (
                              <React.Fragment>
                                <div className={styles.cheapestResultPrice}>
                                  <h4>
                                    {item.derivative.make}{' '}
                                    {item.derivative.model}
                                  </h4>
                                  <ul>
                                    <li>
                                      <span>Personal lease from</span>
                                      <span>
                                        <strong>
                                          {parsedPrice(
                                            item &&
                                              item.leasingData &&
                                              item.leasingData.monthlyPayment &&
                                              item.leasingData.monthlyPayment
                                                .personal
                                          )}
                                        </strong>
                                        &nbsp; per month
                                      </span>
                                    </li>
                                    <li>
                                      <span>Business lease from</span>
                                      <span>
                                        <strong>
                                          {parsedPrice(
                                            item &&
                                              item.leasingData &&
                                              item.leasingData.monthlyPayment &&
                                              item.leasingData.monthlyPayment
                                                .business
                                          )}
                                        </strong>
                                        &nbsp; per month
                                      </span>
                                    </li>
                                  </ul>
                                </div>
                                <div
                                  className={
                                    styles.cheapestResultMobileContainer
                                  }
                                >
                                  <div
                                    className={styles.cheapestResultTitleTab}
                                  >
                                    <h4>Our top deals</h4>
                                    <div className={styles.cheapestResultTab}>
                                      <span>Cheapest lease deal</span>
                                    </div>
                                  </div>
                                  <LeasingCard
                                    key={`${item.derivative.make}-${item.derivative.range}-${index}`}
                                    isMobile={isMobile}
                                    name={getDerivativeDisplayName(
                                      item.derivative
                                    )}
                                    make={item.derivative.make}
                                    range={item.derivative.range}
                                    engine={
                                      item.technical.badgeEngineSizeLitres
                                    }
                                    trim={item.derivative.trim}
                                    personalLeasePrice={parsedPrice(
                                      item &&
                                        item.leasingData &&
                                        item.leasingData.monthlyPayment &&
                                        item.leasingData.monthlyPayment.personal
                                    )}
                                    businessLeaselPrice={parsedPrice(
                                      item &&
                                        item.leasingData &&
                                        item.leasingData.monthlyPayment &&
                                        item.leasingData.monthlyPayment.business
                                    )}
                                    item={item}
                                    fuelType={item.derivative.fuelType}
                                    doors={item.derivative.doors}
                                    transmission={item.derivative.transmission}
                                    gcLid={urlParams.get('gclid')}
                                    network={urlParams.get('network')}
                                    gtmSectionName={'autoTraderClickThrough'}
                                    url={transformLeasingUrl(item.detailsUrl)}
                                    handleClick={this.pushGtm}
                                    portalRef={this.portalRef}
                                    dataSection={thisPageData.dataSection}
                                    productId={
                                      item &&
                                      item.detailsUrl &&
                                      item.detailsUrl.split('/').slice(-1)[0]
                                    }
                                    generation={item.derivative.generation}
                                    derivative={item.derivative.derivative}
                                    bodyType={item.derivative.bodyType}
                                    utm="CARLEASINGDEALSLINKS"
                                    isCheapestCard
                                    leasingPrice={this.state.leasingPrice}
                                  />
                                </div>
                              </React.Fragment>
                            ) : (
                              <React.Fragment>
                                <div className={styles.cheapestResultPrice}>
                                  <ul>
                                    <li>
                                      <span>
                                        Personal lease from
                                        <Icon
                                          iconName="info"
                                          size="medium"
                                          onClick={e => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            this.setState({
                                              enablePriceIndicatorInfo: !this
                                                .state.enablePriceIndicatorInfo,
                                              indicatorType: 'Personal'
                                            });
                                          }}
                                          className={styles.info}
                                        />
                                      </span>
                                      <span>
                                        <strong>
                                          {parsedPrice(
                                            item &&
                                              item.leasingData &&
                                              item.leasingData.monthlyPayment &&
                                              item.leasingData.monthlyPayment
                                                .personal
                                          )}
                                        </strong>
                                        &nbsp; per month
                                      </span>
                                    </li>
                                    <li>
                                      <span>
                                        Business lease from
                                        <Icon
                                          iconName="info"
                                          size="medium"
                                          onClick={e => {
                                            e.stopPropagation();
                                            e.preventDefault();
                                            this.setState({
                                              enablePriceIndicatorInfo: !this
                                                .state.enablePriceIndicatorInfo,
                                              indicatorType: 'Business'
                                            });
                                          }}
                                          className={styles.info}
                                        />
                                      </span>
                                      <span>
                                        <strong>
                                          {parsedPrice(
                                            item &&
                                              item.leasingData &&
                                              item.leasingData.monthlyPayment &&
                                              item.leasingData.monthlyPayment
                                                .business
                                          )}
                                        </strong>
                                        &nbsp; per month
                                      </span>
                                    </li>
                                  </ul>
                                </div>

                                {this.state.enablePriceIndicatorInfo && (
                                  <SimpleModal
                                    closeIcon="close"
                                    onClose={this.setPriceIndicatorInfoLook}
                                    isModalOpen={
                                      this.state.enablePriceIndicatorInfo
                                    }
                                  >
                                    <button
                                      className={styles.closeButton}
                                      onClick={this.setPriceIndicatorInfoLook}
                                    >
                                      Close
                                    </button>

                                    {this.state.indicatorType ===
                                      'Personal' && (
                                      <CarLeaseIndicator
                                        indicatorType="Personal"
                                        expectedAnnualMileage={
                                          item.leasingData.totalMileage
                                        }
                                        initialPayment={
                                          item.leasingData.initialPayment
                                            .personal
                                        }
                                        depositMonths={
                                          item.leasingData.depositMonths
                                        }
                                        termMonths={item.leasingData.termMonths}
                                        monthlyPayment={parsedPrice(
                                          item &&
                                            item.leasingData &&
                                            item.leasingData.monthlyPayment &&
                                            item.leasingData.monthlyPayment
                                              .personal
                                        )}
                                        derivative={item.derivative.derivative}
                                        isMobile={isMobile}
                                      />
                                    )}

                                    {this.state.indicatorType ===
                                      'Business' && (
                                      <CarLeaseIndicator
                                        indicatorType="Business"
                                        expectedAnnualMileage={
                                          item.leasingData.totalMileage
                                        }
                                        initialPayment={
                                          item.leasingData.initialPayment
                                            .business
                                        }
                                        depositMonths={
                                          item.leasingData.depositMonths
                                        }
                                        termMonths={item.leasingData.termMonths}
                                        monthlyPayment={parsedPrice(
                                          item &&
                                            item.leasingData &&
                                            item.leasingData.monthlyPayment &&
                                            item.leasingData.monthlyPayment
                                              .business
                                        )}
                                        derivative={item.derivative.derivative}
                                        isMobile={isMobile}
                                      />
                                    )}
                                  </SimpleModal>
                                )}
                              </React.Fragment>
                            )}
                          </div>
                        ))}
                      </div>
                      {!isMobile && (
                        <div className={styles.cheapestResultDescription}>
                          <h4>Why lease with What Car?</h4>
                          <p className={styles.introText}>{meta.blurb}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </Col>
            </Row>
          )}

          {!isError && (
            <Row className={mtm}>
              <Col colXs={12} colSm={12} colMd={6} colLg={6}>
                <h3
                  className={classNames(
                    classResultsFound,
                    styles.results,
                    styles.leaseResultFound
                  )}
                >
                  Cars found{' '}
                  <span>{count ? count.toLocaleString('en') : 0}</span>
                </h3>
                <div className={styles.toggleStock}>
                  <button
                    onClick={() => this.toggleLeasingPrice('personal')}
                    className={
                      this.state.leasingPrice === 'personal'
                        ? styles.toggleStockActive
                        : ''
                    }
                  >
                    Personal lease
                  </button>
                  <button
                    onClick={() => this.toggleLeasingPrice('business')}
                    className={
                      this.state.leasingPrice === 'business'
                        ? styles.toggleStockActive
                        : ''
                    }
                  >
                    Business lease
                  </button>
                </div>
              </Col>
              {showCheapestResult && !isMobile && (
                <Col
                  colXs={12}
                  colSm={12}
                  colMd={6}
                  colLg={6}
                  className={styles.cheapestResultLogo}
                >
                  {this.renderPartnerLogo()}
                </Col>
              )}
            </Row>
          )}
          {!isError && !isMobile && (
            <Row className={mtm}>
              <Col colXs={12} colSm={12} colMd={12} colLg={12}>
                <ActiveFilters
                  fields={formValues}
                  dealerName={dealerName}
                  activeFilters={activeFilters}
                  removeActiveFilter={this.removeActiveFilter}
                  isMobile={isMobile}
                  resetAllFields={() => this.resetAllFields(formValues)}
                />
              </Col>
            </Row>
          )}
          {!isError && ( // Is mobile
            <Row className={classNames(styles.borderTop, mts)}>
              <Col
                colXs={12}
                colSm={12}
                colMd={4}
                colLg={3}
                className={classNames(
                  styles.filterForm,
                  this.state.isResultsMode && styles.isHidden
                )}
              >
                <div>
                  <div className={mtl}>
                    <FilterForm
                      onSubmit={() => {}}
                      getResults={this.getResults}
                      initialValues={initialValues}
                      activeFilters={activeFilters}
                      fields={formValues}
                      makes={makes}
                      models={models}
                      priceFrom={priceFrom}
                      priceTo={priceTo}
                      additonalOptions={additonalOptions}
                      dealerName={dealerName}
                      fetchUpdatedQuery={this.fetchUpdatedQuery}
                      resultsLength={resultsLength}
                      isMobile={isMobile}
                      removeActiveFilter={this.removeActiveFilter}
                      resetAllFields={() => this.resetAllFields(formValues)}
                      resetField={this.resetField}
                      isListingsPage={true}
                      closeFilters={this.toggleMode}
                      isLeasingPage={isLeasingPage}
                    />
                  </div>
                  <div className={classNames([styles.sideAd, mtl])}>
                    {!isError && isMobile && (
                      <div className={styles.sideAd}>
                        <GPT
                          id={slots.topMpu.id}
                          adUnitPath={createAdUnitPath(adsSeoProps)}
                          collapseEmptyDiv={true}
                          renderWhenViewable={false}
                          sizeMapping={slots.topMpu.sizeMapping}
                          targeting={slots.topMpu.targeting}
                        />
                      </div>
                    )}
                    {!isError && !isMobile && (
                      <GPT
                        id={slots.flexible.id}
                        adUnitPath={createAdUnitPath(adsSeoProps)}
                        collapseEmptyDiv={true}
                        renderWhenViewable={false}
                        sizeMapping={slots.flexible.sizeMapping}
                        targeting={slots.flexible.targeting}
                      />
                    )}
                    {!isError && isMobile && (
                      <React.Fragment>
                        {!this.state.delayAdOverlay2 && (
                          <GPT
                            id={slots.overlay2Mobile.id}
                            adUnitPath={createAdUnitPath(adsSeoProps)}
                            collapseEmptyDiv={true}
                            renderWhenViewable={false}
                            sizeMapping={slots.overlay2Mobile.sizeMapping}
                            targeting={slots.overlay2Mobile.targeting}
                          />
                        )}
                      </React.Fragment>
                    )}
                    {!isError && !isMobile && (
                      <React.Fragment>
                        {!this.state.delayAdOverlay2 && (
                          <GPT
                            id={slots.overlay2.id}
                            adUnitPath={createAdUnitPath(adsSeoProps)}
                            collapseEmptyDiv={true}
                            renderWhenViewable={false}
                            sizeMapping={slots.overlay2.sizeMapping}
                            targeting={slots.overlay2.targeting}
                          />
                        )}
                      </React.Fragment>
                    )}
                  </div>
                </div>
              </Col>
              <Col
                colXs={12}
                colSm={12}
                colMd={8}
                colLg={9}
                className={classNames(
                  styles.resultsGroup,
                  this.state.isResultsMode
                    ? styles.resultsGroupFadeIn
                    : styles.resultsGroupFadeOut
                )}
              >
                {isResultsLoading && (
                  <div className={styles.spinner}>
                    <div className={styles.doubleBounce1} />
                    <div className={styles.doubleBounce2} />
                  </div>
                )}
                <div
                  className={classNames(
                    styles.cards,
                    isResultsLoading ? styles.cardsLoading : null
                  )}
                >
                  {resultsWithAds.length < 1 && !isResultsLoading && (
                    <ErrorBox type={'noResults'} />
                  )}
                  {resultsWithAds}
                  {resultsWithAds.length >= 1 && pagination && (
                    <Pagination
                      fetchUpdatedQuery={this.fetchUpdatedQuery}
                      currentPage={pagination.page}
                      totalPages={Math.ceil(
                        pagination.total / pagination.pageSize
                      )}
                    />
                  )}
                </div>
              </Col>
            </Row>
          )}
          {!isError && (
            <Row className={mtl}>
              <Col colXs={12}>
                <Accordion
                  accordionHeight={175}
                  gtmSectionName={'accordionBrowse'}
                >
                  <BrowseAll
                    items={selectedMake ? models : makes}
                    text="models"
                    make={selectedMake}
                    history={this.props.history}
                    thisPageData={thisPageData}
                  />
                </Accordion>
              </Col>
            </Row>
          )}
        </Grid>
      </Fragment>
    );
  }
}

// $FlowFixMe
export default connect(Results.mapStateToProps)(Results);



// WEBPACK FOOTER //
// ./src/leasingCars/Results/index.js